import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import AdminLayout from "./AdminLayout";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Search, ChevronLeft, ChevronRight, ShieldOff, ShieldCheck, Trash2, Ban, UserCheck, Eye } from "lucide-react";

const SIGNS = ["", "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo", "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"];

function StatusPill({ status, deleted_at }) {
    if (deleted_at) return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/10 text-red-600">Deleted</span>;
    const map = {
        active: "bg-emerald-500/10 text-emerald-600",
        blocked: "bg-red-500/10 text-red-600",
        deactivated: "bg-amber-500/10 text-amber-700",
    };
    const cls = map[status] || "bg-ink/5 text-ink-muted";
    return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${cls}`}>{status || "active"}</span>;
}

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("active");
    const [sign, setSign] = useState("");
    const [sort, setSort] = useState("created_desc");
    const [page, setPage] = useState(1);
    const [pageSize] = useState(25);
    const [selected, setSelected] = useState(new Set());
    const [confirm, setConfirm] = useState(null);  // { kind, payload, title, body, action }

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                status, sort, page: String(page), page_size: String(pageSize),
            });
            if (q.trim()) params.set("q", q.trim());
            if (sign) params.set("sign", sign);
            const r = await api.get(`/admin/users?${params}`);
            setUsers(r.data.users);
            setTotal(r.data.total);
        } finally { setLoading(false); }
    }, [q, status, sign, sort, page, pageSize]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const toggleSelect = (id) => {
        setSelected((s) => {
            const next = new Set(s);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        if (selected.size === users.length) setSelected(new Set());
        else setSelected(new Set(users.map((u) => u.user_id)));
    };

    const runSingle = async (userId, kind) => {
        await api.post(`/admin/users/${userId}/${kind}`);
        fetchUsers();
    };
    const runBulk = async (action) => {
        await api.post("/admin/users/bulk", { user_ids: [...selected], action });
        setSelected(new Set());
        fetchUsers();
    };

    const confirmSingle = (u, kind, title, body, action) => {
        setConfirm({
            title, body, action: action || "Confirm",
            run: async () => { await runSingle(u.user_id, kind); setConfirm(null); },
        });
    };
    const confirmBulk = (kind, title, body) => {
        setConfirm({
            title, body, action: "Confirm",
            run: async () => { await runBulk(kind); setConfirm(null); },
        });
    };

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <AdminLayout title="User Management" subtitle={`${total} ${status === "all" ? "total" : status} users`}>
            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center mb-4">
                <div className="relative flex-1 min-w-[220px]">
                    <Search strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
                    <input
                        value={q}
                        onChange={(e) => { setQ(e.target.value); setPage(1); }}
                        placeholder="Search by email, name, or user ID…"
                        className="sp-input pl-10 w-full"
                        data-testid="admin-users-search"
                    />
                </div>
                <select className="sp-input" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} data-testid="admin-users-status">
                    <option value="active">Active</option>
                    <option value="blocked">Blocked</option>
                    <option value="deactivated">Deactivated</option>
                    <option value="deleted">Deleted</option>
                    <option value="all">All</option>
                </select>
                <select className="sp-input" value={sign} onChange={(e) => { setSign(e.target.value); setPage(1); }} data-testid="admin-users-sign">
                    {SIGNS.map((s) => <option key={s || "any"} value={s}>{s || "Any sign"}</option>)}
                </select>
                <select className="sp-input" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="admin-users-sort">
                    <option value="created_desc">Newest</option>
                    <option value="created_asc">Oldest</option>
                    <option value="name_asc">Name A-Z</option>
                    <option value="email_asc">Email A-Z</option>
                </select>
            </div>

            {/* Bulk actions */}
            {selected.size > 0 && (
                <div className="flex items-center gap-3 px-4 py-3 mb-3 bg-ink/5 border border-ink/10 rounded-xl" data-testid="admin-bulk-bar">
                    <span className="text-sm">{selected.size} selected</span>
                    <button className="sp-btn-ghost text-xs" onClick={() => confirmBulk("block", "Block selected users?", "They will be unable to sign in until unblocked.")} data-testid="bulk-block">Block</button>
                    <button className="sp-btn-ghost text-xs" onClick={() => confirmBulk("unblock", "Unblock selected users?", "They will regain access immediately.")} data-testid="bulk-unblock">Unblock</button>
                    <button className="sp-btn-ghost text-xs text-red-600" onClick={() => confirmBulk("delete", "Delete selected users?", "Records are soft-deleted (deleted_at timestamp) and not removed from MongoDB.")} data-testid="bulk-delete">Delete</button>
                </div>
            )}

            {/* Table */}
            <div className="bg-surface border border-ink/10 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-ink/10 text-xs text-ink-muted uppercase tracking-wider">
                                <th className="px-3 py-3 w-10">
                                    <input type="checkbox" checked={users.length > 0 && selected.size === users.length} onChange={toggleAll} data-testid="admin-users-select-all" />
                                </th>
                                <th className="text-left px-3 py-3">User</th>
                                <th className="text-left px-3 py-3">Signs</th>
                                <th className="text-left px-3 py-3">Saved</th>
                                <th className="text-left px-3 py-3">Status</th>
                                <th className="text-left px-3 py-3">Created</th>
                                <th className="text-right px-3 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody data-testid="admin-users-table-body">
                            {users.map((u) => (
                                <tr key={u.user_id} className="border-b border-ink/5 hover:bg-ink/[0.02]" data-testid={`admin-user-row-${u.user_id}`}>
                                    <td className="px-3 py-3">
                                        <input type="checkbox" checked={selected.has(u.user_id)} onChange={() => toggleSelect(u.user_id)} data-testid={`admin-user-select-${u.user_id}`} />
                                    </td>
                                    <td className="px-3 py-3">
                                        <Link to={`/admin/users/${u.user_id}`} className="hover:underline">
                                            <div className="font-medium">{u.name || "—"} {u.is_admin && <span className="ml-2 text-[9px] uppercase tracking-wider bg-planet-sun/20 text-planet-sun px-1.5 py-0.5 rounded-full">Admin</span>}</div>
                                            <div className="text-xs text-ink-muted">{u.email}</div>
                                        </Link>
                                    </td>
                                    <td className="px-3 py-3 text-xs text-ink-muted">
                                        ☉ {u.sun || "—"} · ☽ {u.moon || "—"} · ↑ {u.rising || "—"}
                                    </td>
                                    <td className="px-3 py-3 tabular-nums">{u.saved_count}</td>
                                    <td className="px-3 py-3"><StatusPill status={u.status} deleted_at={u.deleted_at} /></td>
                                    <td className="px-3 py-3 text-xs text-ink-muted">{(u.created_at || "").slice(0, 10)}</td>
                                    <td className="px-3 py-3">
                                        <div className="flex items-center gap-1.5 justify-end">
                                            <Link to={`/admin/users/${u.user_id}`} className="p-1.5 hover:bg-ink/10 rounded" aria-label="View" data-testid={`action-view-${u.user_id}`}>
                                                <Eye strokeWidth={1.5} className="w-4 h-4" />
                                            </Link>
                                            {u.status !== "blocked" ? (
                                                <button className="p-1.5 hover:bg-ink/10 rounded" onClick={() => confirmSingle(u, "block", "Block this user?", `${u.email} will be unable to sign in.`, "Block")} aria-label="Block" data-testid={`action-block-${u.user_id}`}>
                                                    <Ban strokeWidth={1.5} className="w-4 h-4" />
                                                </button>
                                            ) : (
                                                <button className="p-1.5 hover:bg-ink/10 rounded" onClick={() => runSingle(u.user_id, "unblock")} aria-label="Unblock" data-testid={`action-unblock-${u.user_id}`}>
                                                    <ShieldCheck strokeWidth={1.5} className="w-4 h-4" />
                                                </button>
                                            )}
                                            {u.status !== "deactivated" ? (
                                                <button className="p-1.5 hover:bg-ink/10 rounded" onClick={() => confirmSingle(u, "deactivate", "Deactivate this user?", `${u.email} will be marked inactive.`, "Deactivate")} aria-label="Deactivate" data-testid={`action-deactivate-${u.user_id}`}>
                                                    <ShieldOff strokeWidth={1.5} className="w-4 h-4" />
                                                </button>
                                            ) : (
                                                <button className="p-1.5 hover:bg-ink/10 rounded" onClick={() => runSingle(u.user_id, "reactivate")} aria-label="Reactivate" data-testid={`action-reactivate-${u.user_id}`}>
                                                    <UserCheck strokeWidth={1.5} className="w-4 h-4" />
                                                </button>
                                            )}
                                            <button className="p-1.5 hover:bg-red-500/10 rounded text-red-600" onClick={() => confirmSingle(u, "delete", "Delete this user permanently?", "Soft-delete: a deleted_at timestamp will be added. The record is NOT removed from MongoDB.", "Delete User")} aria-label="Delete" data-testid={`action-delete-${u.user_id}`}>
                                                <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {!loading && users.length === 0 && (
                                <tr><td colSpan={7} className="px-3 py-10 text-center text-ink-muted text-sm">No users match these filters.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-ink/10 text-xs text-ink-muted">
                    <span>Page {page} of {totalPages}</span>
                    <div className="flex items-center gap-1">
                        <button className="p-1.5 hover:bg-ink/10 rounded disabled:opacity-40" disabled={page === 1} onClick={() => setPage((p) => p - 1)} data-testid="admin-users-prev">
                            <ChevronLeft strokeWidth={1.5} className="w-4 h-4" />
                        </button>
                        <button className="p-1.5 hover:bg-ink/10 rounded disabled:opacity-40" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="admin-users-next">
                            <ChevronRight strokeWidth={1.5} className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
                <AlertDialogContent data-testid="admin-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
                        <AlertDialogDescription>{confirm?.body}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="admin-confirm-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => confirm?.run()} data-testid="admin-confirm-action">{confirm?.action}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </AdminLayout>
    );
}
